// Cadre commun des écrans d'un dossier d'étude : fil de contexte, titre, statut, navigation entre les étapes.
// Utilisé par les modules Études, Conception et Devis. Contrat API : GET /api/dossiers/:id →
// { id, reference, title, status, demo, site: { id, name }, client: { name } | null, compteurs?: Record<string, number> }
import { useId, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Archive, ChevronDown, FlaskConical } from 'lucide-react';
import { api } from '../../lib/api.ts';
import { Badge, Chargement, EtatErreur, FilContexte } from '../../components/ui.tsx';
import { BadgeStatutDossier } from '../../components/statuts.tsx';
import { BandeauClasse, MenuDossier, type ClassementDossier } from './FinDeDossier.tsx';

export interface DossierEntete {
  id: string; reference: string; title: string; status: string; demo: boolean;
  site: { id: string; name: string }; client: { name: string } | null; compteurs?: Record<string, number>;
  /** Classé sans suite : lecture seule jusqu'à la réouverture (null = dossier actif). */
  classement?: ClassementDossier | null;
}

export type EtapeDossier = 'synthese' | 'pieces' | 'exigences' | 'perimetre' | 'ecarts' | 'conception' | 'devis' | 'memoire';

const ETAPES: { id: EtapeDossier; libelle: string; chemin: string; compteur?: string }[] = [
  { id: 'synthese', libelle: 'Synthèse', chemin: '' },
  { id: 'pieces', libelle: 'Pièces', chemin: '/pieces', compteur: 'pieces' },
  { id: 'exigences', libelle: 'Exigences', chemin: '/exigences', compteur: 'exigences' },
  { id: 'perimetre', libelle: 'Périmètre', chemin: '/perimetre' },
  { id: 'ecarts', libelle: 'Écarts et questions', chemin: '/ecarts', compteur: 'ecarts' },
  { id: 'conception', libelle: 'Conception', chemin: '/conception' },
  { id: 'devis', libelle: 'Devis', chemin: '/devis', compteur: 'devis' },
  { id: 'memoire', libelle: 'Mémoire', chemin: '/memoire', compteur: 'memoires' },
];

export function useDossier(dossierId: string | undefined) {
  return useQuery({ queryKey: ['dossier', dossierId], enabled: !!dossierId, queryFn: () => api.get<DossierEntete>(`/api/dossiers/${dossierId}`) });
}

export function DossierCadre({ dossierId, actif, actions, children, large }:
  { dossierId: string; actif: EtapeDossier; actions?: ReactNode; children: ReactNode; large?: boolean }) {
  const q = useDossier(dossierId);
  if (q.isLoading) return <Chargement lignes={5} />;
  if (q.isError || !q.data) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  return <Cadre x={q.data} actif={actif} actions={actions} large={large}>{children}</Cadre>;
}

/* Sur téléphone (≤ 600 px), le décor cède la place à la fonction : l'en-tête tient en deux lignes (titre, référence et statut)
   et les étapes en une seule (l'étape en cours) ; le fil de contexte, les actions de l'en-tête et la liste des 8 étapes
   s'ouvrent dans un volet. Au-delà de 600 px, rien ne change : tout est affiché, les boutons de repli sont masqués. */
function Cadre({ x, actif, actions, children, large }:
  { x: DossierEntete; actif: EtapeDossier; actions?: ReactNode; children: ReactNode; large?: boolean }) {
  const [details, setDetails] = useState(false);
  const [etapes, setEtapes] = useState(false);
  const idDetails = useId();
  const idFil = useId();
  const idEtapes = useId();
  const base = `/etudes/${x.id}`;
  const rang = ETAPES.findIndex((e) => e.id === actif);
  const etape = ETAPES[rang];
  const compteur = (e: (typeof ETAPES)[number]) =>
    e.compteur && x.compteurs?.[e.compteur] !== undefined ? <span className="compteur">{x.compteurs[e.compteur]}</span> : null;
  return (
    <div className={`pile-l dossier-cadre ${large ? 'dossier-large' : ''}`}>
      <header className="entete-page dossier-entete" data-ouvert={details}>
        <div className="entete-page-texte">
          <div className="dossier-fil" id={idFil}>
            <FilContexte noeuds={[...(x.client ? [{ type: 'Client', libelle: x.client.name }] : []),
              { type: 'Site', libelle: x.site.name, to: `/sites/${x.site.id}` }, { type: 'Dossier', libelle: x.reference }]} />
          </div>
          <div className="dossier-titre-ligne">
            <h1 className="dossier-titre" title={x.title}><span className="dossier-titre-texte">{x.title}</span>
              {x.demo && <span className="dossier-demo-long"><Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />}>Démonstration — données fictives</Badge></span>}</h1>
            <button type="button" className="btn btn-fantome btn-icone dossier-repli-bouton" aria-expanded={details}
              aria-controls={`${idFil} ${idDetails}`} onClick={() => setDetails((v) => !v)}
              aria-label={details ? 'Masquer les détails du dossier' : 'Afficher les détails du dossier (contexte et actions)'}>
              <ChevronDown aria-hidden className="dossier-chevron" />
            </button>
          </div>
          <p className="secondaire dossier-meta">
            <span className="mono">{x.reference}</span><BadgeStatutDossier statut={x.status} />
            {x.classement && <Badge ton="neutre" icone={<Archive size={14} aria-hidden />}>Classé sans suite</Badge>}
            {x.demo && <span className="dossier-demo-court"><Badge ton="ambre" icone={<FlaskConical size={14} aria-hidden />} titre="Démonstration — données fictives">Démo</Badge></span>}
          </p>
        </div>
        <div className="groupe-btn dossier-actions" id={idDetails}>{actions}<MenuDossier dossier={x} /></div>
      </header>
      {x.classement && <BandeauClasse dossierId={x.id} classement={x.classement} />}
      {/* Étapes : toutes visibles à partir de 601 px (retour à la ligne, jamais de défilement horizontal) ;
          sur téléphone, une seule ligne « Étape : … » qui déplie la liste complète. */}
      <div className="dossier-etapes-bloc" data-ouvert={etapes}>
        {etape && (
          <button type="button" className="btn dossier-etapes-bouton" aria-expanded={etapes} aria-controls={idEtapes} onClick={() => setEtapes((v) => !v)}>
            <span className="discret">Étape</span>
            <strong>{etape.libelle}</strong>{compteur(etape)}
            <span className="discret dossier-etapes-rang">{`${rang + 1} sur ${ETAPES.length}`}</span>
            <ChevronDown aria-hidden className="dossier-chevron" />
          </button>
        )}
        <nav className="onglets dossier-etapes" id={idEtapes} aria-label="Étapes du dossier">
          {ETAPES.map((e) => (
            <NavLink key={e.id} to={`${base}${e.chemin}`} end className="onglet" aria-current={e.id === actif ? 'page' : undefined}
              style={e.id === actif ? { color: 'var(--encre)', borderBottomColor: 'var(--accent)', textDecoration: 'none' } : { textDecoration: 'none' }}>
              {e.libelle}{compteur(e)}
            </NavLink>
          ))}
        </nav>
      </div>
      {children}
    </div>
  );
}
